import { Injectable, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { HttpClient } from '@angular/common/http'
import { Observable, BehaviorSubject, map, tap } from 'rxjs'
import { environment } from '../../../environments/environment'
import { User, UserRole } from '../models/models'

@Injectable({ providedIn: 'root' })
export class UserService {
  private http = inject(HttpClient)

  /**
   * The currently signed-in user. Set by AuthCallbackComponent after the
   * Cognito Hosted UI flow completes, and by authGuard on page reload from
   * the stored JWT tokens via AuthService.
   */
  private _currentUser$ = new BehaviorSubject<User | null>(null)
  currentUser$ = this._currentUser$.asObservable()

  get currentUser(): User | null {
    return this._currentUser$.value
  }

  set currentUser(user: User | null) {
    this._currentUser$.next(user)
  }

  /** Cache populated by loadUsers(). Used for synchronous lookups in templates. */
  users: User[] = []

  /**
   * Fetches all users from the API and caches them locally.
   * Call this in ngOnInit() of any component that needs user data.
   */
  loadUsers(): Observable<void> {
    return this.http.get<User[]>(`${environment.apiUrl}/users`).pipe(
      tap(users => { this.users = users }),
      map(() => void 0),
    )
  }

  /**
   * Loads the user cache and invokes `onSettled` on both success and error,
   * wiring teardown to the caller's DestroyRef. Convenience wrapper around
   * loadUsers() for the common "load then trigger change detection" pattern.
   */
  ensureUsersLoaded(onSettled: () => void, destroyRef: DestroyRef): void {
    this.loadUsers().pipe(takeUntilDestroyed(destroyRef)).subscribe({
      next: onSettled,
      error: (err) => {
        console.error('Failed to load users:', err)
        onSettled()
      },
    })
  }

  getUserById(id: string | null): User | undefined {
    if (!id) return undefined
    return this.users.find(u => u.id === id)
  }

  /** Returns the display name for a user ID, or '—' for null/unknown. */
  getUserName(id: string | null): string {
    if (!id) return '—'
    return this.users.find(u => u.id === id)?.name ?? '—'
  }

  /** Returns all non-deleted users who have the Editor role. Sync from cache. */
  getEditors(): User[] {
    return this.users.filter(u => u.roles.includes('Editor') && !u.is_deleted)
  }

  /**
   * Creates a new user in Cognito and DB, then adds them to the local cache.
   */
  addUser(name: string, email: string, roles: UserRole[]): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/users`, { name, email, roles }).pipe(
      tap(user => { this.users.push(user) }),
    )
  }

  /**
   * Updates the Cognito group memberships for a user, then updates the local cache.
   */
  changeRoles(userId: string, roles: UserRole[]): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/users/${userId}/roles`, { roles }).pipe(
      tap(() => {
        const i = this.users.findIndex(u => u.id === userId)
        if (i !== -1) this.users[i] = { ...this.users[i], roles }
      }),
    )
  }

  /**
   * Sets a new permanent password for a user (admin action) via Cognito.
   */
  resetPassword(userId: string, password: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/users/${userId}/reset-password`, { password })
  }

  /**
   * Hard-deletes the user's Cognito account and soft-deletes the DB row.
   * On success, marks the cached entry deleted (roles cleared) rather than
   * removing it, so historical name lookups keep resolving.
   */
  deleteUser(userId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/users/${userId}`).pipe(
      tap(() => {
        const i = this.users.findIndex(u => u.id === userId)
        if (i !== -1) this.users[i] = { ...this.users[i], is_deleted: true, roles: [] }
      }),
    )
  }
}
